import React, { useState, useMemo } from 'react';
import {
  Calculator,
  AlertTriangle,
  CheckCircle2,
  Download,
  Sparkles,
  ArrowRight,
  X,
  Info,
} from 'lucide-react';
import { toast } from '../utils/toast';
import type { Plan } from '../types';

export interface PricingQuotaSimulatorProps {
  productName: string;
  plans: Plan[];
  onSelectPlan?: (planId: string) => void;
  isOpen?: boolean;
  onClose?: () => void;
  isModal?: boolean;
}

export interface PlanSimulationResult {
  plan: Plan;
  baseCost: number;
  overageCalls: number;
  overageCost: number;
  totalCostUsd: number;
  totalCostAoa: number;
  unitCostUsd: number;
  quotaExceeded: boolean;
  isRecommended: boolean;
  savingsUsd: number;
}

const USD_TO_AOA_RATE = 925; // Câmbio de referência oficial USD -> AOA

export const calculatePlanSimulation = (
  plan: Plan,
  simulatedVolume: number
): {
  baseCost: number;
  overageCalls: number;
  overageCost: number;
  totalCostUsd: number;
  totalCostAoa: number;
  unitCostUsd: number;
  quotaExceeded: boolean;
} => {
  const baseCost = Number(plan.monthly_price || 0);
  const quota = Number(plan.monthly_quota || 0);
  const overageCalls = Math.max(0, simulatedVolume - quota);
  const perTx = Number(plan.per_transaction_price || 0);

  let overageCost = 0;
  let totalCostUsd = 0;
  let quotaExceeded = false;

  if (plan.pricing_model === 'per_transaction') {
    totalCostUsd = simulatedVolume * perTx;
  } else if (plan.pricing_model === 'monthly') {
    totalCostUsd = baseCost;
    if (simulatedVolume > quota) {
      quotaExceeded = true;
    }
  } else {
    // Hybrid: base monthly + overage price
    overageCost = overageCalls * perTx;
    totalCostUsd = baseCost + overageCost;
  }

  const totalCostAoa = totalCostUsd * USD_TO_AOA_RATE;
  const unitCostUsd = simulatedVolume > 0 ? totalCostUsd / simulatedVolume : 0;

  return {
    baseCost,
    overageCalls,
    overageCost,
    totalCostUsd: Number(totalCostUsd.toFixed(2)),
    totalCostAoa: Math.round(totalCostAoa),
    unitCostUsd: Number(unitCostUsd.toFixed(5)),
    quotaExceeded,
  };
};

export const PricingQuotaSimulator: React.FC<PricingQuotaSimulatorProps> = ({
  productName,
  plans,
  onSelectPlan,
  isOpen = true,
  onClose,
  isModal = false,
}) => {
  const [simulatedVolume, setSimulatedVolume] = useState<number>(50000);

  const presets = [
    { label: '10k (Início)', value: 10000 },
    { label: '50k (Padrão)', value: 50000 },
    { label: '250k (Crescimento)', value: 250000 },
    { label: '1M (Escala)', value: 1000000 },
    { label: '3M (Enterprise)', value: 3000000 },
  ];

  // Evaluate simulation for all plans
  const simulationResults = useMemo<PlanSimulationResult[]>(() => {
    if (!plans || plans.length === 0) return [];

    const computed = plans.map((plan) => {
      const calc = calculatePlanSimulation(plan, simulatedVolume);
      return {
        plan,
        ...calc,
        isRecommended: false,
        savingsUsd: 0,
      };
    });

    // Determine the recommended plan (lowest total cost among plans that do not suffer hard HTTP 429 quota block, or lowest overall)
    const eligiblePlans = computed.filter((c) => !c.quotaExceeded || c.plan.pricing_model === 'hybrid' || c.plan.pricing_model === 'per_transaction');
    const targetPool = eligiblePlans.length > 0 ? eligiblePlans : computed;

    const minCostPlan = targetPool.reduce((prev, curr) =>
      curr.totalCostUsd < prev.totalCostUsd ? curr : prev
    );

    const maxCost = Math.max(...computed.map((c) => c.totalCostUsd));

    return computed.map((c) => ({
      ...c,
      isRecommended: c.plan.id === minCostPlan.plan.id,
      savingsUsd: Number(Math.max(0, maxCost - c.totalCostUsd).toFixed(2)),
    }));
  }, [plans, simulatedVolume]);

  const recommendedResult = simulationResults.find((r) => r.isRecommended);

  const handleExportCsv = () => {
    const header = [
      'Produto',
      'Plano',
      'Modelo de Preco',
      'Volume Simulado',
      'Quota Incluida',
      'Chamadas Excedentes',
      'Custo Base (USD)',
      'Custo Excedente (USD)',
      'Custo Total (USD)',
      'Custo Total Estimado (AOA)',
      'Recomendado',
    ].join(';');

    const rows = simulationResults.map((r) =>
      [
        `"${productName}"`,
        `"${r.plan.tier.toUpperCase()}"`,
        `"${r.plan.pricing_model}"`,
        simulatedVolume,
        r.plan.monthly_quota,
        r.overageCalls,
        r.baseCost.toFixed(2),
        r.overageCost.toFixed(2),
        r.totalCostUsd.toFixed(2),
        r.totalCostAoa,
        r.isRecommended ? 'SIM' : 'NAO',
      ].join(';')
    );

    const csvContent = '\uFEFF' + [header, ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `simulacao_custos_${productName.toLowerCase().replace(/[^a-z0-9]/g, '_')}_${simulatedVolume}_req.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Estimativa comercial exportada com sucesso (CSV)!');
  };

  if (!isOpen) return null;

  const content = (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-primary-100 dark:bg-primary-950/50 flex items-center justify-center text-primary-600 dark:text-primary-400">
              <Calculator className="w-4 h-4" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">
              Simulador de Cobrança & Quotas
            </h3>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Projete o volume mensal de requisições para a API <strong>{productName}</strong> e encontre o plano financeiramente ideal.
          </p>
        </div>

        <div className="flex items-center gap-2 self-end sm:self-auto">
          <button
            onClick={handleExportCsv}
            className="btn-base btn-secondary btn-sm text-xs inline-flex items-center gap-1.5"
            title="Exportar estimativa em folha de cálculo CSV"
          >
            <Download className="w-3.5 h-3.5" />
            <span>Exportar Proposta (CSV)</span>
          </button>
          {isModal && onClose && (
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Interactive Volume Controls Card */}
      <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          <div>
            <label htmlFor="volume-slider" className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 block">
              Volume Estimado de Chamadas / Mês
            </label>
            <span className="text-2xl font-black text-slate-900 dark:text-white mt-0.5 block">
              {simulatedVolume.toLocaleString('pt-PT')} <span className="text-sm font-normal text-slate-400">requisições/mês</span>
            </span>
          </div>

          {/* Quick Presets */}
          <div className="flex flex-wrap gap-1.5">
            {presets.map((p) => (
              <button
                key={p.value}
                onClick={() => setSimulatedVolume(p.value)}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                  simulatedVolume === p.value
                    ? 'bg-primary-600 text-white shadow-xs'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Range Slider */}
        <input
          id="volume-slider"
          type="range"
          min="1000"
          max="3000000"
          step="5000"
          value={simulatedVolume}
          onChange={(e) => setSimulatedVolume(Number(e.target.value))}
          className="w-full h-2.5 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-primary-600"
        />

        <div className="flex justify-between text-[11px] text-slate-400 font-mono">
          <span>1.000 req/mês</span>
          <span>1.500.000 req/mês</span>
          <span>3.000.000 req/mês</span>
        </div>
      </div>

      {/* Smart Recommendation Banner */}
      {recommendedResult && (
        <div className="p-4 rounded-2xl bg-gradient-to-r from-emerald-500/10 via-primary-500/10 to-teal-500/10 border border-emerald-300 dark:border-emerald-800/80 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0 shadow-sm">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                  Recomendação Inteligente de Custo
                </span>
                <span className="badge badge-success">Plano {recommendedResult.plan.tier.toUpperCase()}</span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">
                Para <strong>{simulatedVolume.toLocaleString('pt-PT')} req/mês</strong>, o plano <strong>{recommendedResult.plan.tier.toUpperCase()}</strong> oferece o menor custo total: <strong>${recommendedResult.totalCostUsd.toFixed(2)}/mês</strong> (~Kz {recommendedResult.totalCostAoa.toLocaleString('pt-PT')}).
              </p>
            </div>
          </div>

          {onSelectPlan && (
            <button
              onClick={() => onSelectPlan(recommendedResult.plan.id)}
              className="btn-base btn-primary btn-sm whitespace-nowrap text-xs px-4 py-2 self-end sm:self-auto inline-flex items-center gap-1.5"
            >
              <span>Subscrever {recommendedResult.plan.tier.toUpperCase()}</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {/* Plans Comparison Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {simulationResults.map((res) => {
          const { plan, totalCostUsd, totalCostAoa, overageCalls, overageCost, quotaExceeded, isRecommended } = res;
          const quota = plan.monthly_quota;
          const quotaPercent = Math.min(100, Math.round((simulatedVolume / quota) * 100));

          return (
            <div
              key={plan.id}
              className={`p-5 rounded-2xl border flex flex-col justify-between transition-all bg-white dark:bg-slate-900 ${
                isRecommended
                  ? 'border-emerald-500 dark:border-emerald-500 ring-2 ring-emerald-500/20 shadow-md'
                  : 'border-slate-200 dark:border-slate-800'
              }`}
            >
              <div className="space-y-3">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-bold uppercase tracking-wider text-sm text-slate-900 dark:text-white">
                      {plan.tier}
                    </span>
                    <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500">
                      {plan.pricing_model}
                    </span>
                  </div>
                  {isRecommended && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 px-2.5 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-800">
                      <CheckCircle2 className="w-3 h-3" /> Melhor Custo
                    </span>
                  )}
                </div>

                {/* Simulated Price */}
                <div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-black text-slate-900 dark:text-white">
                      ${totalCostUsd.toFixed(2)}
                    </span>
                    <span className="text-xs text-slate-400">/mês</span>
                  </div>
                  <p className="text-xs text-slate-500 font-mono mt-0.5">
                    ~ Kz {totalCostAoa.toLocaleString('pt-PT')} <span className="text-[10px] text-slate-400">(câmbio Kz {USD_TO_AOA_RATE}/$)</span>
                  </p>
                </div>

                {/* Quota Gauge */}
                <div className="space-y-1 pt-1">
                  <div className="flex justify-between text-[11px]">
                    <span className="text-slate-500">Quota: {quota.toLocaleString('pt-PT')} req</span>
                    <span
                      className={`font-semibold ${
                        quotaPercent > 95
                          ? 'text-rose-600 dark:text-rose-400'
                          : quotaPercent > 80
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-slate-600 dark:text-slate-300'
                      }`}
                    >
                      {quotaPercent}% consumida
                    </span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${
                        quotaPercent > 95
                          ? 'bg-rose-500'
                          : quotaPercent > 80
                          ? 'bg-amber-500'
                          : 'bg-primary-500'
                      }`}
                      style={{ width: `${quotaPercent}%` }}
                    />
                  </div>
                </div>

                {/* Cost Breakdown */}
                <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl space-y-1.5 text-xs">
                  <div className="flex justify-between text-slate-600 dark:text-slate-400">
                    <span>Mensalidade Base:</span>
                    <span className="font-mono">${plan.monthly_price || 0}</span>
                  </div>
                  <div className="flex justify-between text-slate-600 dark:text-slate-400">
                    <span>Chamadas Excedentes:</span>
                    <span className="font-mono">
                      {overageCalls > 0 ? `+${overageCalls.toLocaleString('pt-PT')}` : '0'}
                    </span>
                  </div>
                  {overageCalls > 0 && (
                    <div className="flex justify-between text-slate-600 dark:text-slate-400">
                      <span>Custo Excedente:</span>
                      <span className="font-mono text-emerald-600 dark:text-emerald-400">
                        +${overageCost.toFixed(2)} (${plan.per_transaction_price || 0}/req)
                      </span>
                    </div>
                  )}
                </div>

                {/* Warning if hard quota limit exceeded */}
                {quotaExceeded && (
                  <div className="p-2.5 rounded-lg bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 text-xs text-rose-800 dark:text-rose-300 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                    <span>
                      <strong>Limite Estrito:</strong> Este plano não permite transações excedentes. Requisições acima de {quota.toLocaleString('pt-PT')} retornarão <code>HTTP 429</code>.
                    </span>
                  </div>
                )}
              </div>

              {/* Action Button */}
              {onSelectPlan && (
                <div className="pt-4 mt-3 border-t border-slate-100 dark:border-slate-800">
                  <button
                    onClick={() => onSelectPlan(plan.id)}
                    className={`w-full py-2 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                      isRecommended
                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs'
                        : 'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-800 dark:text-slate-200'
                    }`}
                  >
                    <span>Subscrever {plan.tier.toUpperCase()}</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Informative Note */}
      <div className="p-3.5 bg-blue-50/60 dark:bg-blue-950/30 rounded-xl border border-blue-200 dark:border-blue-900/60 flex items-start gap-2.5 text-xs text-blue-900 dark:text-blue-200">
        <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
        <p className="leading-relaxed">
          Os valores simulados são estimativas baseadas no modelo contratual da API. O saldo é debitado da carteira da empresa conforme o tráfego em tempo real ou debitado mensalmente. O câmbio oficial de referência é de <strong>1 USD = {USD_TO_AOA_RATE} AOA</strong>.
        </p>
      </div>
    </div>
  );

  if (isModal) {
    return (
      <div
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
        role="dialog"
        aria-modal="true"
      >
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl max-w-4xl w-full p-6 sm:p-8 shadow-2xl my-8 max-h-[90vh] overflow-y-auto">
          {content}
        </div>
      </div>
    );
  }

  return content;
};
